import { hasColour } from './diagnoseSegments';

// Overlay the SITE DISEASE COLOUR KEY onto a family's disease list
// (TGCLOUD-1793 Phase 2).
//
// THIS IS THE ONE PLACE THE KEY IS APPLIED ON THE READ PATH, and that is the
// whole point. The plan's top risk is resolving the key in the legend but not in
// the node fill, or the reverse: they read different documents and always have,
// and a partial override looks like it worked on whichever diseases you happened
// to test while the chart quietly contradicts its own key.
//
// The two consumers converge here because both descend from the same family
// disease list:
//   - the LEGEND renders that list's colours as its swatches, and
//   - the NODE FILL resolves through `diseaseColorMap`, which is built from the
//     same list (useCarrierStatus) and consulted by `resolveDiseaseColour`
//     (diagnoseSegments, TGCLOUD-1504 remediation B4).
// Overlaying once, upstream of the fork, is therefore sufficient AND necessary.
// Applying it at either consumer instead would reintroduce exactly the
// asymmetry B4 was written to close.
//
// DECISION C: this is a RENDER-TIME overlay. Nothing here is written back. The
// stored `diagnoses[].color` on every member document is left exactly as it is,
// so configuring, changing or removing a key never rewrites patient data and
// never emits an audit entry — and unpinning a disease restores the automatic
// colour with no migration.
//
// DECISION taken 2026-09-18: the SITE KEY WINS, always, including over a
// clinician's per-family colour edit. A key that a per-family edit can override
// is not a key, and "the same disease is the same colour everywhere" is the
// entire request. The cost is real and should be surfaced in the Phase 3 UI: a
// clinician's deliberate correction stops having any visible effect once an
// admin pins that disease.

/**
 * @param {object[]} diseases  the family disease list (`users.diagnoses`)
 * @param {object}   colourKey `{ [diseaseId]: hex }`, empty when the feature is
 *                             off or the group has pinned nothing
 * @returns {object[]} the same array reference when nothing is pinned
 */
export function applyColourKey(diseases, colourKey) {
  if (!Array.isArray(diseases) || diseases.length === 0) return diseases;
  if (!colourKey || Object.keys(colourKey).length === 0) return diseases;

  let changed = false;
  const next = diseases.map((d) => {
    if (!d || d.id == null) return d;
    const pinned = colourKey[String(d.id)];
    // An empty or malformed pin must not blank a colour: a colourless disease
    // paints solid black and reads as Bennett "affected".
    if (!hasColour(pinned) || pinned === d.color) return d;
    changed = true;
    return { ...d, color: pinned };
  });

  // Same reference when nothing moved, so the memos downstream of this do not
  // invalidate and force a full repaint on every render.
  return changed ? next : diseases;
}

export default applyColourKey;
